import type {
  HeroSelection,
  LotDetailResponse,
  LotListItem,
  LotLiveState,
  LotTier,
} from '@skinstep/shared';
import { TIMER_INITIAL_MS } from '@skinstep/shared';
import { parseSkinMarketName, type WeaponKind, type Wear } from '@skinstep/shared/skins';
import type { Bidder, Lot } from '../data/mockLots';

const TIER_PALETTE: Record<LotTier, { from: string; via: string; to: string; accent: string }> = {
  A: { from: '#1a0d05', via: '#d97520', to: '#fff', accent: '#ff8c1a' },
  B: { from: '#0a0014', via: '#3a0d6e', to: '#0fc6e8', accent: '#b537ff' },
  C: { from: '#0a0000', via: '#1a0202', to: '#ff1a1a', accent: '#ff4444' },
};

const FALLBACK_KIND_AS_MOCK: Record<WeaponKind, Lot['weapon']> = {
  sniper: 'sniper',
  rifle: 'rifle',
  pistol: 'pistol',
  smg: 'smg',
  knife: 'knife',
  heavy: 'rifle',
  other: 'rifle',
};

const FALLBACK_WEAR: Wear = 'FT';

export function placeholderBidder(
  userId: number | null,
  firstName: string | null = null,
  username: string | null = null,
  bidIndex?: number,
): Bidder {
  const id = userId ?? 0;
  const name = firstName?.trim() || (id ? `Player #${id}` : 'No bids yet');
  const initial = name && name !== 'No bids yet' ? name.charAt(0).toUpperCase() : '?';
  return {
    id: id ? `u${id}` : 'u?',
    firstName: name,
    username: username?.trim() ? username : null,
    initial,
    hue: (Math.abs(id) * 53) % 360,
    bidCount: 0,
    bidIndex,
  };
}

function deriveCode(id: string, tier: LotTier): string {
  const tail =
    id
      .replace(/[^a-z0-9]/gi, '')
      .slice(-3)
      .toUpperCase() || '000';
  return `${tier}-${tail}`;
}

export function lotFromApi(item: LotListItem): Lot {
  const sourceName = item.market_hash_name ?? item.skin_market_name;
  const parsed = parseSkinMarketName(sourceName);
  const palette = TIER_PALETTE[item.tier];
  const marketPriceCents = item.market_price_cents ?? item.skin_market_price_cents;
  const marketPrice = marketPriceCents / 100;
  const live = item.live_state;

  // The DO owns the live timer. Without live_state we never fabricate a
  // deadline: a started lot falls back to its real go-live time + the initial
  // window (reads ~60s until the next 1s poll brings real live_state), and a
  // not-yet-started lot has no timer — the value is unused because
  // deriveLotState reports it as 'scheduled' off `startedAt`.
  const baseTimerEndsAt = live
    ? live.timer_expires_at
    : item.started_at != null
      ? item.started_at + TIMER_INITIAL_MS
      : item.scheduled_start_at;

  const lastBidder = placeholderBidder(
    live?.last_bidder_user_id ?? null,
    live?.last_bidder_first_name ?? null,
    live?.last_bidder_username ?? null,
  );

  const winner =
    item.winner_user_id != null
      ? placeholderBidder(item.winner_user_id, item.winner_first_name, item.winner_username)
      : undefined;

  return {
    id: item.id,
    code: deriveCode(item.id, item.tier),
    tier: item.tier,
    weapon: FALLBACK_KIND_AS_MOCK[parsed.weaponKind],
    weaponLabel: parsed.weapon,
    skinName: parsed.skinName || item.skin_name || '',
    // Rarity comes from `rarity_color` (catalog-sourced). When absent we leave
    // it null and let LotHero render a neutral chip — tier ≠ rarity, so we
    // don't guess one from the other.
    rarity: null,
    wear: parsed.wear ?? FALLBACK_WEAR,
    marketPrice,
    currentPrice: live ? live.current_price_cents / 100 : (item.final_price_cents ?? 0) / 100,
    bidsCount: live?.bids_count ?? item.final_bids_count ?? 0,
    timerEndsAt: baseTimerEndsAt,
    startedAt: item.started_at ?? undefined,
    startsAt: item.status === 'scheduled' ? item.scheduled_start_at : undefined,
    closedAt: item.closed_at ?? undefined,
    finalPrice: item.final_price_cents != null ? item.final_price_cents / 100 : undefined,
    lastBidder,
    lastBidderSeq: live?.last_bidder_user_bid_seq ?? undefined,
    winner,
    recentBidders: (live?.recent_bidders ?? []).map((rb) =>
      placeholderBidder(rb.user_id, rb.first_name, rb.username, rb.bid_index),
    ),
    totalBidders: live?.bids_count ?? item.final_bids_count ?? 0,
    uniqueBidders: live?.unique_bidders_count ?? item.final_unique_bidders ?? 0,
    palette,
    // image_url is the catalog-sourced URL (added in migration 0004); older
    // rows still only have skin_image_url. Fall back so legacy lots render too.
    imageUrl: item.image_url ?? item.skin_image_url,
    rarityColor: item.rarity_color ?? null,
    isStatTrak: parsed.isStatTrak,
    isSouvenir: parsed.isSouvenir,
  };
}

export type FetchLotsParams = { status?: 'scheduled' | 'live' | 'closed' };
export type LotsListResponse = { lots: LotListItem[]; hero: HeroSelection | null };
// The status=closed view also carries `total`: the platform-wide count of
// played lots, powering the "Recent wins" header so it stays honest even though
// the list itself is capped server-side. It exists only on this view, so it's a
// separate type rather than an optional field on the shared response.
export type ClosedLotsResponse = LotsListResponse & { total: number };
export type { LotDetailResponse };

export function applyLiveState(lot: Lot, live: LotLiveState | null): Lot {
  if (!live) return lot;
  return {
    ...lot,
    currentPrice: live.current_price_cents / 100,
    bidsCount: live.bids_count,
    timerEndsAt: live.timer_expires_at,
    startedAt: live.started_at,
    lastBidder: placeholderBidder(
      live.last_bidder_user_id,
      live.last_bidder_first_name,
      live.last_bidder_username,
    ),
    lastBidderSeq: live.last_bidder_user_bid_seq ?? undefined,
    recentBidders: live.recent_bidders.map((rb) =>
      placeholderBidder(rb.user_id, rb.first_name, rb.username, rb.bid_index),
    ),
    totalBidders: live.bids_count,
    uniqueBidders: live.unique_bidders_count,
    closedAt: live.closed_at ?? lot.closedAt,
  };
}

export function lotFromDetail(detail: LotDetailResponse): Lot {
  const listItem: LotListItem = { ...detail.lot, live_state: detail.live_state };
  return lotFromApi(listItem);
}
